import React, { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDynamicPermissions } from '../../hooks/useDynamicPermissions';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { ArrowLeftIcon, ChevronUpDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import RichTextEditor from '../../components/RichTextEditor';
import { Combobox, Transition } from '@headlessui/react';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { trackCaseOperation } from '../../utils/appInsights';
import { useIsServiceDeskUser } from '../../hooks/useIsServiceDeskUser';
import { useEngagementResources } from '../../hooks/useEngagementResources';
import { fetchAssignmentGroupMembers } from '../../utils/groupMembers';

interface FormData {
  shortDescription: string;
  description: string;
  channel: string;
  category: string;
  customerId: string;
  contactId: string;
  engagementId: string;
  servicePlanId: string;
  resourceId: string;
  otherResourceNames: string;
  impact: string;
  urgency: string;
  assignmentGroupId: string;
  assignedTo: string;
  createdBy: string;
  caseType: string;
}

const CaseCreate: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can, loading: permissionsLoading } = useDynamicPermissions();
  const hasPermission = can('cases', 'create');
  const { formatDateTime } = useDateFormatter();
  const isServiceDeskUser = useIsServiceDeskUser();

  // Priority calculation function (matches backend logic)
  const calculatePriorityFromImpactUrgency = (impact: string, urgency: string): string => {
    if (!impact || !urgency) return 'low_4'; // Default to Low
    
    const impactNum = parseInt(impact.split('_')[1]);
    const urgencyNum = parseInt(urgency.split('_')[1]);
    
    if (impactNum === 1 && urgencyNum === 1) return 'critical_1';
    if ((impactNum === 1 && urgencyNum === 2) || (impactNum === 2 && urgencyNum === 1)) return 'high_2';
    if ((impactNum === 1 && urgencyNum === 3) || (impactNum === 2 && urgencyNum === 2) || (impactNum === 3 && urgencyNum === 1)) return 'moderate_3';
    if ((impactNum === 2 && urgencyNum === 3) || (impactNum === 3 && urgencyNum === 2)) return 'low_4';
    if (impactNum === 3 && urgencyNum === 3) return 'planning_5';
    
    return 'low_4'; // Default to Low
  };
  const { user } = useAuth();
  const [formData, setFormData] = useState<FormData>({
    shortDescription: '',
    description: '',
    channel: 'web',
    category: 'issue',
    customerId: '',
    contactId: '',
    engagementId: '',
    servicePlanId: '',
    resourceId: '',
    impact: 'low_3',
    urgency: 'low_3',
    assignmentGroupId: '',
    assignedTo: '',
    createdBy: '',
    caseType: 'case',
    otherResourceNames: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedResourceIdValue, setSelectedResourceIdValue] = useState<string>('');
  const [isOtherResource, setIsOtherResource] = useState<boolean>(false);
  const [pinnedResource, setPinnedResource] = useState<{
    id: string;
    name: string;
    resourceId?: string;
  } | null>(null);

  // Combobox query states
  const [customerQuery, setCustomerQuery] = useState('');
  const [contactQuery, setContactQuery] = useState('');
  const [engagementQuery, setEngagementQuery] = useState('');
  const [servicePlanQuery, setServicePlanQuery] = useState('');
  const [resourceQuery, setResourceQuery] = useState('');
  const [assignmentGroupQuery, setAssignmentGroupQuery] = useState('');
  const [assignedToQuery, setAssignedToQuery] = useState('');

  // Check if user is a contact user
  const isContactUser = user?.class?.toLowerCase() === 'contact';

  // State to track whether engagement and contact should be editable for contact users
  const [contactUserHasMultipleEngagements, setContactUserHasMultipleEngagements] = useState(false);
  const [contactUserHasMultipleContacts, setContactUserHasMultipleContacts] = useState(false);

  // Initialize form data for contact users - only set customerId, let engagement/contact be populated after fetching
  useEffect(() => {
    if (isContactUser && user) {
      setFormData(prev => ({
        ...prev,
        customerId: user.customerId || ''
      }));
    }
  }, [isContactUser, user]);

  // Fetch group members for assignment dropdown - only when assignment group is selected
  const { data: groupMembersData } = useQuery({
    queryKey: ['group-members', formData.assignmentGroupId, formData.customerId],
    queryFn: async () => {
      if (!formData.assignmentGroupId) return [];
      return fetchAssignmentGroupMembers(formData.assignmentGroupId, formData.customerId || null);
    },
    enabled: !!formData.assignmentGroupId
  });

  // Fetch assignment groups for dropdown (only groups with type 'assignment')
  // Filter by engagement if available
  const { data: assignmentGroupsData } = useQuery({
    queryKey: ['assignment-groups', formData.engagementId],
    queryFn: async () => {
      const params = formData.engagementId ? `?engagementId=${formData.engagementId}` : '';
      const response = await api.get(`/groups/assignment-groups${params}`);
      return response.data;
    },
    enabled: !isContactUser || !!formData.engagementId // For contact users, only fetch when engagementId is available
  });

  // Fetch service desk group for contact users - auto-select and disable
  const { data: serviceDeskGroupData } = useQuery({
    queryKey: ['service-desk-group'],
    queryFn: async () => {
      const response = await api.get('/groups/service-desk');
      return response.data;
    },
    enabled: isContactUser
  });

  // Auto-select service desk group for contact users
  useEffect(() => {
    if (isContactUser && serviceDeskGroupData?.id && !formData.assignmentGroupId) {
      setFormData(prev => ({
        ...prev,
        assignmentGroupId: serviceDeskGroupData.id
      }));
    }
  }, [isContactUser, serviceDeskGroupData, formData.assignmentGroupId]);

  // Fetch customers for dropdown
  // For contact users, we create a mock customer option from user data; for others, fetch all customers
  const { data: customersData } = useQuery({
    queryKey: ['customers', isContactUser ? user?.customerId : 'all'],
    queryFn: async () => {
      if (isContactUser && user?.customerId) {
        // For contact users, create a customer option from user data to avoid permission issues
        return {
          customers: [{
            id: user.customerId,
            accountName: user.customerName || `Customer ${user.customerId.substring(0, 8)}`
          }]
        };
      } else {
        // Fetch all customers for non-contact users
        const response = await api.get('/customers');
        return response.data;
      }
    },
    enabled: true // Always enabled since contact users use mock data
  });

  // Fetch contacts for dropdown - filter by customer and/or engagement
  // Uses the contacts dropdown endpoint which doesn't require permissions
  const { data: contactsData } = useQuery({
    queryKey: ['contacts-dropdown', formData.customerId, formData.engagementId],
    queryFn: async () => {
      // Build query params based on available filters
      const params = new URLSearchParams();

      if (formData.customerId) {
        params.append('customerId', formData.customerId);
      }
      if (formData.engagementId) {
        params.append('engagementId', formData.engagementId);
      }

      const response = await api.get(`/contacts/dropdown?${params.toString()}`);
      return response.data;
    },
    enabled: !!formData.customerId || !!formData.engagementId
  });

  // Auto-select contact for contact users if only one exists, and track multiple contacts state
  useEffect(() => {
    if (isContactUser && contactsData?.contacts && formData.engagementId) {
      const contacts = contactsData.contacts;
      if (contacts.length === 1) {
        // Single contact - auto-select and disable
        setContactUserHasMultipleContacts(false);
        if (!formData.contactId) {
          setFormData(prev => ({
            ...prev,
            contactId: contacts[0].id
          }));
        }
      } else if (contacts.length > 1) {
        // Multiple contacts - allow selection
        setContactUserHasMultipleContacts(true);
      }
    }
  }, [isContactUser, contactsData, formData.engagementId, formData.contactId]);

  // Fetch engagements for dropdown - when customer is selected
  // Uses the dropdown endpoint which doesn't require engagements:read permission
  const { data: engagementsData } = useQuery({
    queryKey: ['engagements-dropdown', formData.customerId],
    queryFn: async () => {
      if (formData.customerId) {
        // Use dropdown endpoint - works for both contact and non-contact users
        const response = await api.get(`/engagements/dropdown?customerId=${formData.customerId}`);
        return response.data;
      }
      return { engagements: [] };
    },
    enabled: !!formData.customerId
  });

  // Auto-select engagement for contact users if only one exists, and track multiple engagement state
  useEffect(() => {
    if (isContactUser && engagementsData?.engagements) {
      const engagements = engagementsData.engagements;
      if (engagements.length === 1) {
        // Single engagement - auto-select and disable
        setContactUserHasMultipleEngagements(false);
        if (!formData.engagementId) {
          setFormData(prev => ({
            ...prev,
            engagementId: engagements[0].id
          }));
        }
      } else if (engagements.length > 1) {
        // Multiple engagements - allow selection
        setContactUserHasMultipleEngagements(true);
      }
    }
  }, [isContactUser, engagementsData, formData.engagementId]);

  // Fetch service plans for dropdown - filter by customer if available
  const { data: servicePlansData } = useQuery({
    queryKey: ['service-plans', formData.customerId],
    queryFn: async () => {
      const params = formData.customerId ? `?customerId=${formData.customerId}` : '';
      const response = await api.get(`/service-plans${params}`);
      return response.data;
    },
    enabled: !isContactUser || !!formData.customerId // For contact users, only fetch when customerId is available
  });

  // Fetch resources for dropdown - ServiceNow CIs when engagement is SN-configured
  const { data: engagementResources } = useEngagementResources(
    formData.engagementId,
    resourceQuery,
    pinnedResource
  );
  const resourcesData = engagementResources?.resources || [];
  const isServiceNowResourceSource = engagementResources?.source === 'servicenow';

  // Safely extract resources array
  const resources = Array.isArray(resourcesData) ? resourcesData : [];

  // Fetch primary contact for auto-fill when customer/engagement is selected (only for non-contact users)
  const { data: primaryContactData } = useQuery({
    queryKey: ['primary-contact', formData.customerId, formData.engagementId],
    queryFn: async () => {
      if (!formData.customerId) return { contact: null };
      const params = new URLSearchParams({ customerId: formData.customerId });
      if (formData.engagementId) {
        params.append('engagementId', formData.engagementId);
      }
      const response = await api.get(`/contacts/primary?${params.toString()}`);
      return response.data;
    },
    enabled: !!formData.customerId && !isContactUser // Only for non-contact users
  });

  // Auto-fill contact when customer/engagement is selected and primary contact is found (only for non-contact users)
  useEffect(() => {
    if (!isContactUser && formData.customerId && primaryContactData?.contact && !formData.contactId) {
      setFormData(prev => ({
        ...prev,
        contactId: primaryContactData.contact.id
      }));
    }
  }, [formData.customerId, formData.engagementId, primaryContactData, formData.contactId, isContactUser]);

  const createCaseMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const response = await api.post('/cases', data);
      return response.data;
    },
    onSuccess: async (data) => {
      // Invalidate list queries so they refetch when user navigates back
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['cases'] }),
        queryClient.invalidateQueries({ queryKey: ['tickets'] })
      ]);
      
      // Log Case Created
      if (user) {
        const caseNumber = data?.case?.number || data?.number || data?.id || data?._id || 'Unknown';
        trackCaseOperation(
          'Case Created',
          user.email || user.firstName || 'Unknown',
          {
            action: 'Case Created',
            caseNumber: caseNumber,
          },
          {
            firstName: user.firstName,
            lastName: user.lastName,
            role: user.role,
            roleType: user.roleType,
            roleLevel: user.roleLevel,
            userClass: user.class,
            customerId: user.customerId,
            customerName: user.customerName,
            engagementId: user.engagementId,
            engagementName: user.engagementName,
            isServiceDeskUser: isServiceDeskUser,
          }
        );
      }
      
      // Contact users should be routed to tickets view, others to cases view
      const targetPath = isContactUser ? `/tickets/${data.id || data._id}` : `/cases/${data.id || data._id}`;
      navigate(targetPath);
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to create case';
      setErrors({ submit: message });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const newErrors: Record<string, string> = {};
    if (!formData.shortDescription.trim()) {
      newErrors.shortDescription = 'Short Description is required';
    }
    if (!formData.channel) {
      newErrors.channel = 'Channel is required';
    }
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    if (!formData.impact) {
      newErrors.impact = 'Impact is required';
    }
    if (!formData.urgency) {
      newErrors.urgency = 'Urgency is required';
    }
    if (!formData.customerId) {
      newErrors.customerId = 'Customer is required';
    }
    if (!formData.engagementId) {
      newErrors.engagementId = 'Engagement is required';
    }
    if (!formData.resourceId && !isOtherResource) {
      newErrors.resourceId = formData.engagementId
        ? 'Resource is required (select a resource or choose "Others")'
        : 'Please select an engagement first';
    }
    if (isOtherResource && !formData.otherResourceNames.trim()) {
      newErrors.otherResourceNames = 'Please enter at least one resource name';
    }
    if (!formData.assignmentGroupId) {
      newErrors.assignmentGroupId = 'Assignment Group is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Clean up empty fields and calculate priority
    const calculatedPriority = calculatePriorityFromImpactUrgency(formData.impact, formData.urgency);
    const submitData: any = {
      shortDescription: formData.shortDescription,
      channel: formData.channel,
      category: formData.category,
      impact: formData.impact,
      urgency: formData.urgency,
      priority: calculatedPriority
    };
    
    if (formData.description) submitData.description = formData.description;
    if (formData.customerId) submitData.customerId = formData.customerId;
    if (formData.contactId) submitData.contactId = formData.contactId;
    if (formData.engagementId) submitData.engagementId = formData.engagementId;
    if (formData.servicePlanId) submitData.servicePlanId = formData.servicePlanId;

    // Add resource - either resourceId OR otherResourceNames (mutually exclusive)
    if (isOtherResource && formData.otherResourceNames.trim()) {
      submitData.otherResourceNames = formData.otherResourceNames.trim();
    } else if (formData.resourceId) {
      submitData.resourceId = formData.resourceId;
    }

    if (formData.assignedTo) submitData.assignedTo = formData.assignedTo;
    if (formData.assignmentGroupId) submitData.assignmentGroupId = formData.assignmentGroupId;
    if (user?.id) submitData.createdBy = user.id;
    if (formData.caseType) submitData.caseType = formData.caseType;
    
    createCaseMutation.mutate(submitData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newFormData = {
        ...prev,
        [name]: value
      };

      // Reset dependent fields when parent fields change
      if (name === 'customerId') {
        newFormData.contactId = '';
        newFormData.engagementId = '';
        newFormData.servicePlanId = '';
        newFormData.resourceId = '';
        newFormData.assignmentGroupId = '';
        newFormData.assignedTo = '';
      }
      if (name === 'engagementId') {
        newFormData.contactId = ''; // Clear contact since it's filtered by engagement
        newFormData.servicePlanId = '';
        newFormData.resourceId = '';
        newFormData.otherResourceNames = '';
        newFormData.assignmentGroupId = ''; // Clear assignment group since it's filtered by engagement
        newFormData.assignedTo = '';
        setIsOtherResource(false);
        setSelectedResourceIdValue('');
        setPinnedResource(null);
      }
      if (name === 'assignmentGroupId') {
        newFormData.assignedTo = '';
      }

      return newFormData;
    });
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleDescriptionChange = (value: string) => {
    setFormData(prev => ({ ...prev, description: value }));
    if (errors.description) {
      setErrors(prev => ({ ...prev, description: '' }));
    }
  };

  const categories = [
    { value: 'question', label: 'Question' },
    { value: 'issue', label: 'Issue' },
    { value: 'request', label: 'Request' },
    { value: 'change', label: 'Change' }
  ];

  const channels = [
    { value: 'web', label: 'Web' },
    { value: 'phone', label: 'Phone' },
    { value: 'email', label: 'Email' },
    { value: 'alert', label: 'Alert' }
  ];

  const impacts = [
    { value: 'high_1', label: '1 - High' },
    { value: 'medium_2', label: '2 - Medium' },
    { value: 'low_3', label: '3 - Low' }
  ];

  const urgencies = [
    { value: 'high_1', label: '1 - High' },
    { value: 'medium_2', label: '2 - Medium' },
    { value: 'low_3', label: '3 - Low' }
  ];

  // SLA functionality has been hidden - no SLA-related fields are displayed

  // Show loading while checking permissions
  if (permissionsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Check permission
  if (!hasPermission) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          You don't have permission to create cases.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-8 py-6">
        <div className="mb-6">
          <button
            onClick={() => navigate('/cases')}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to Cases
          </button>
        </div>

        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h1 className="text-xl font-semibold text-gray-900">Create New Case</h1>
          </div>

          <form onSubmit={handleSubmit} className="p-8">
            {errors.submit && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-800">{errors.submit}</p>
              </div>
            )}

            {/* Row 1: Number and Opened */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Number
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value=""
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Opened
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value={formatDateTime(new Date())}
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 2: Channel and Category */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="channel" className="block text-sm font-medium text-gray-700 mb-1">
                      Channel <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <select
                      id="channel"
                      name="channel"
                      value={formData.channel}
                      onChange={handleChange}
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        errors.channel 
                          ? 'border-red-300 focus:ring-red-400' 
                          : 'border-gray-300'
                      }`}
                    >
                      {channels.map(channel => (
                        <option key={channel.value} value={channel.value}>{channel.label}</option>
                      ))}
                    </select>
                    {errors.channel && (
                      <p className="mt-1 text-sm text-red-600">{errors.channel}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                      Category <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <select
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        errors.category 
                          ? 'border-red-300 focus:ring-red-400' 
                          : 'border-gray-300'
                      }`}
                    >
                      {categories.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                    {errors.category && (
                      <p className="mt-1 text-sm text-red-600">{errors.category}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 3: State */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State
                    </label>
                  </div>
                  <div className="col-span-9">
                    <select
                      value="new"
                      disabled
                      className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 bg-gray-100"
                    >
                      <option value="new">New</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Row 4: Impact and Urgency */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="impact" className="block text-sm font-medium text-gray-700 mb-1">
                      Impact <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <select
                      id="impact"
                      name="impact"
                      value={formData.impact}
                      onChange={handleChange}
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        errors.impact 
                          ? 'border-red-300 focus:ring-red-400' 
                          : 'border-gray-300'
                      }`}
                    >
                      {impacts.map(impact => (
                        <option key={impact.value} value={impact.value}>{impact.label}</option>
                      ))}
                    </select>
                    {errors.impact && (
                      <p className="mt-1 text-sm text-red-600">{errors.impact}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="urgency" className="block text-sm font-medium text-gray-700 mb-1">
                      Urgency <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <select
                      id="urgency"
                      name="urgency"
                      value={formData.urgency}
                      onChange={handleChange}
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        errors.urgency 
                          ? 'border-red-300 focus:ring-red-400' 
                          : 'border-gray-300'
                      }`}
                    >
                      {urgencies.map(urgency => (
                        <option key={urgency.value} value={urgency.value}>{urgency.label}</option>
                      ))}
                    </select>
                    {errors.urgency && (
                      <p className="mt-1 text-sm text-red-600">{errors.urgency}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 5: Priority */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value={(() => {
                        const priority = calculatePriorityFromImpactUrgency(formData.impact, formData.urgency);
                        switch (priority) {
                          case 'critical_1': return '1 - Critical';
                          case 'high_2': return '2 - High';
                          case 'moderate_3': return '3 - Moderate';
                          case 'low_4': return '4 - Low';
                          case 'planning_5': return '5 - Planning';
                          default: return '4 - Low';
                        }
                      })()}
                      disabled
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 6: Customer and Engagement */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="customerId" className="block text-sm font-medium text-gray-700 mb-1">
                      Customer <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.customerId || ''}
                      onChange={(value: string) => {
                        setFormData(prev => ({
                          ...prev,
                          customerId: value || '',
                          contactId: '',
                          engagementId: '',
                          servicePlanId: '',
                          resourceId: '',
                          assignmentGroupId: '',
                          assignedTo: ''
                        }));
                        setIsOtherResource(false);
                        setSelectedResourceIdValue('');
                        setPinnedResource(null);
                        if (errors.customerId) {
                          setErrors(prev => ({ ...prev, customerId: '' }));
                        }
                      }}
                      disabled={isContactUser}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${isContactUser ? 'bg-gray-50 text-gray-400' : ''} ${errors.customerId ? 'border-red-300' : 'border-gray-300'}`}
                          displayValue={(customerId: string) => {
                            const customer = (customersData?.customers || []).find((c: any) => c.id === customerId);
                            return customer ? customer.accountName : '';
                          }}
                          onChange={(e) => setCustomerQuery(e.target.value)}
                          placeholder="Select Customer"
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setCustomerQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {(customersData?.customers || [])
                              .filter((customer: any) => customer.accountName.toLowerCase().includes(customerQuery.toLowerCase()))
                              .map((customer: any) => (
                              <Combobox.Option
                                key={customer.id}
                                value={customer.id}
                                className={({ active }) =>
                                  `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                }
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                      {customer.accountName}
                                    </span>
                                    {selected && (
                                      <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                        <CheckIcon className="h-4 w-4" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Combobox.Option>
                            ))}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                    {errors.customerId && (
                      <p className="mt-1 text-sm text-red-600">{errors.customerId}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="engagementId" className="block text-sm font-medium text-gray-700 mb-1">
                      Engagement <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.engagementId || ''}
                      onChange={(value: string) => {
                        setFormData(prev => ({
                          ...prev,
                          engagementId: value || '',
                          contactId: '',
                          servicePlanId: '',
                          resourceId: '',
                          assignmentGroupId: '',
                          assignedTo: ''
                        }));
                        // Reset contact multiple state so auto-selection can run again
                        setContactUserHasMultipleContacts(false);
                        setIsOtherResource(false);
                        setSelectedResourceIdValue('');
                        setPinnedResource(null);
                        if (errors.engagementId) {
                          setErrors(prev => ({ ...prev, engagementId: '' }));
                        }
                      }}
                      disabled={(isContactUser && !contactUserHasMultipleEngagements) || !formData.customerId}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${((isContactUser && !contactUserHasMultipleEngagements) || !formData.customerId) ? 'bg-gray-50 text-gray-400' : ''} ${errors.engagementId ? 'border-red-300' : 'border-gray-300'}`}
                          displayValue={(engagementId: string) => {
                            const engagement = (engagementsData?.engagements || []).find((e: any) => e.id === engagementId);
                            return engagement ? engagement.name : '';
                          }}
                          onChange={(e) => setEngagementQuery(e.target.value)}
                          placeholder="Select Engagement"
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setEngagementQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {(engagementsData?.engagements || [])
                              .filter((engagement: any) => engagement.name.toLowerCase().includes(engagementQuery.toLowerCase()))
                              .map((engagement: any) => (
                              <Combobox.Option
                                key={engagement.id}
                                value={engagement.id}
                                className={({ active }) =>
                                  `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                }
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                      {engagement.name}
                                    </span>
                                    {selected && (
                                      <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                        <CheckIcon className="h-4 w-4" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Combobox.Option>
                            ))}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                    {errors.engagementId && (
                      <p className="mt-1 text-sm text-red-600">{errors.engagementId}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 7: Contact and Service Plan */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="contactId" className="block text-sm font-medium text-gray-700 mb-1">
                      Contact
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.contactId || ''}
                      onChange={(value: string) => {
                        setFormData(prev => ({
                          ...prev,
                          contactId: value || ''
                        }));
                        if (errors.contactId) {
                          setErrors(prev => ({ ...prev, contactId: '' }));
                        }
                      }}
                      disabled={(isContactUser && !contactUserHasMultipleContacts) || !formData.engagementId}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${((isContactUser && !contactUserHasMultipleContacts) || !formData.engagementId) ? 'bg-gray-50 text-gray-400' : ''} ${errors.contactId ? 'border-red-300' : 'border-gray-300'}`}
                          displayValue={(contactId: string) => {
                            const contact = (contactsData?.contacts || []).find((c: any) => c.id === contactId);
                            return contact ? (contact.name || contact.email) : '';
                          }}
                          onChange={(e) => setContactQuery(e.target.value)}
                          placeholder="Select Contact"
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setContactQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {(contactsData?.contacts || [])
                              .filter((contact: any) => `${contact.name || ''} ${contact.email || ''}`.toLowerCase().includes(contactQuery.toLowerCase()))
                              .map((contact: any) => (
                              <Combobox.Option
                                key={contact.id}
                                value={contact.id}
                                className={({ active }) =>
                                  `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                }
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                      {contact.name || contact.email}
                                    </span>
                                    {selected && (
                                      <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                        <CheckIcon className="h-4 w-4" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Combobox.Option>
                            ))}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                    {errors.contactId && (
                      <p className="mt-1 text-sm text-red-600">{errors.contactId}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="servicePlanId" className="block text-sm font-medium text-gray-700 mb-1">
                      Service Plan
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.servicePlanId}
                      onChange={(value) => setFormData({ ...formData, servicePlanId: value || '' })}
                      disabled={!formData.engagementId}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-gray-50 disabled:text-gray-400"
                          displayValue={(servicePlanId: string) => {
                            const plan = (servicePlansData?.plans || []).find((p: any) => p.id === servicePlanId);
                            return plan ? plan.name : '';
                          }}
                          onChange={(e) => setServicePlanQuery(e.target.value)}
                          placeholder={formData.engagementId ? "Select Service Plan" : "Select Engagement first"}
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setServicePlanQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {(servicePlansData?.plans || [])
                              .filter((plan: any) => plan && plan.id && plan.name && plan.name.toLowerCase().includes(servicePlanQuery.toLowerCase()))
                              .map((plan: any) => (
                                <Combobox.Option
                                  key={plan.id}
                                  value={plan.id}
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                      active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ selected, active }) => (
                                    <>
                                      <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                        {plan.name}
                                      </span>
                                      {selected && (
                                        <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                          active ? 'text-white' : 'text-indigo-600'
                                        }`}>
                                          <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Combobox.Option>
                              ))}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                  </div>
                </div>
              </div>
            </div>

            {/* Row 8: Resource and Resource ID / Custom Resources */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="resourceId" className="block text-sm font-medium text-gray-700 mb-1">
                      Resource <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={isOtherResource ? '__OTHER__' : (formData.resourceId || '')}
                      onChange={(value: string) => {
                        if (value === '__OTHER__') {
                          setIsOtherResource(true);
                          setPinnedResource(null);
                          setFormData(prev => ({ ...prev, resourceId: '' }));
                          setSelectedResourceIdValue('');
                        } else {
                          setIsOtherResource(false);
                          const selectedResource = resources.find((r: any) => r.id === value);
                          setPinnedResource(
                            selectedResource
                              ? {
                                  id: selectedResource.id,
                                  name: selectedResource.name,
                                  resourceId: selectedResource.resourceId,
                                }
                              : null
                          );
                          setSelectedResourceIdValue(selectedResource?.resourceId || '');
                          setFormData(prev => ({ ...prev, resourceId: value || '', otherResourceNames: '' }));
                        }
                        if (errors.resourceId) {
                          setErrors(prev => ({ ...prev, resourceId: '' }));
                        }
                        if (errors.otherResourceNames) {
                          setErrors(prev => ({ ...prev, otherResourceNames: '' }));
                        }
                      }}
                      disabled={!formData.engagementId}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${!formData.engagementId ? 'bg-gray-50 text-gray-400' : ''} ${errors.resourceId ? 'border-red-300' : 'border-gray-300'}`}
                          displayValue={(resourceId: string) => {
                            if (isOtherResource) return 'Others (specify custom resources)';
                            const resource = resources.find((r: any) => r.id === resourceId);
                            return resource?.name || pinnedResource?.name || '';
                          }}
                          onChange={(e) => setResourceQuery(e.target.value)}
                          placeholder="Select Resource"
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setResourceQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {resources
                              .filter((resource: any) => {
                                if (!resource?.name) return false;
                                if (isServiceNowResourceSource) return true;
                                return resource.name
                                  .toLowerCase()
                                  .includes(resourceQuery.toLowerCase());
                              })
                              .map((resource: any) => (
                              <Combobox.Option
                                key={resource.id}
                                value={resource.id}
                                className={({ active }) =>
                                  `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                }
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                      {resource.name}
                                    </span>
                                    {selected && (
                                      <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                        <CheckIcon className="h-4 w-4" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Combobox.Option>
                            ))}
                            {/* Others option */}
                            <Combobox.Option
                              key="__OTHER__"
                              value="__OTHER__"
                              className={({ active }) =>
                                `relative cursor-pointer select-none py-2 pl-3 pr-9 border-t border-gray-200 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                              }
                            >
                              {({ selected, active }) => (
                                <>
                                  <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                    Others (specify custom resources)
                                  </span>
                                  {selected && (
                                    <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                      <CheckIcon className="h-4 w-4" />
                                    </span>
                                  )}
                                </>
                              )}
                            </Combobox.Option>
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                    {errors.resourceId && (
                      <p className="mt-1 text-sm text-red-600">{errors.resourceId}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className={`grid grid-cols-12 gap-4 ${isOtherResource ? 'items-start' : 'items-end'}`}>
                  <div className="col-span-3">
                    <label className={`block text-sm font-medium text-gray-700 ${isOtherResource ? 'mt-1' : 'mb-1'}`}>
                      {isOtherResource ? 'Custom Resources' : 'Resource ID'}
                      {isOtherResource && <span className="text-red-500"> *</span>}
                    </label>
                  </div>
                  <div className="col-span-9">
                    {isOtherResource ? (
                      <>
                        <input
                          type="text"
                          value={formData.otherResourceNames}
                          onChange={(e) => {
                            setFormData(prev => ({ ...prev, otherResourceNames: e.target.value }));
                            if (errors.otherResourceNames) {
                              setErrors(prev => ({ ...prev, otherResourceNames: '' }));
                            }
                          }}
                          placeholder="Enter resource names, separated by commas (e.g., Server-01, Database-02)"
                          className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${errors.otherResourceNames ? 'border-red-300' : 'border-gray-300'}`}
                        />
                        {errors.otherResourceNames && (
                          <p className="mt-1 text-sm text-red-600">{errors.otherResourceNames}</p>
                        )}
                        <p className="mt-1 text-xs text-gray-500">Enter multiple resource names separated by commas</p>
                      </>
                    ) : (
                      <input
                        type="text"
                        value={selectedResourceIdValue}
                        readOnly
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 9: Assignment Group and Assigned To */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="assignmentGroupId" className="block text-sm font-medium text-gray-700 mb-1">
                      Assignment Group <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.assignmentGroupId || ''}
                      onChange={(value: string) => {
                        setFormData(prev => ({
                          ...prev,
                          assignmentGroupId: value || '',
                          assignedTo: ''
                        }));
                      }}
                      disabled={isContactUser}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className={`w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${isContactUser ? 'bg-gray-50 text-gray-500' : ''}`}
                          displayValue={(groupId: string) => {
                            // For contact users, try to show service desk group name first
                            if (isContactUser && serviceDeskGroupData?.id === groupId) {
                              return serviceDeskGroupData.name;
                            }
                            const group = (assignmentGroupsData || []).find((g: any) => g.id === groupId);
                            return group ? group.name : (serviceDeskGroupData?.name || '');
                          }}
                          onChange={(e) => setAssignmentGroupQuery(e.target.value)}
                          placeholder="Select Assignment Group"
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setAssignmentGroupQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {(assignmentGroupsData || [])
                              .filter((group: any) => group.name.toLowerCase().includes(assignmentGroupQuery.toLowerCase()))
                              .map((group: any) => (
                              <Combobox.Option
                                key={group.id}
                                value={group.id}
                                className={({ active }) =>
                                  `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                }
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                      {group.name}
                                    </span>
                                    {selected && (
                                      <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                        <CheckIcon className="h-4 w-4" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Combobox.Option>
                            ))}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                  </div>
                </div>
              </div>
              {/* Hide Assigned To field for contact users */}
              {!isContactUser && (
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="assignedTo" className="block text-sm font-medium text-gray-700 mb-1">
                      Assigned To
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.assignedTo || ''}
                      onChange={(value: string) => {
                        setFormData(prev => ({
                          ...prev,
                          assignedTo: value || ''
                        }));
                        if (errors.assignedTo) {
                          setErrors(prev => ({ ...prev, assignedTo: '' }));
                        }
                      }}
                      disabled={!formData.assignmentGroupId}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${!formData.assignmentGroupId ? 'bg-gray-50 text-gray-400' : ''} ${errors.assignedTo ? 'border-red-300' : 'border-gray-300'}`}
                          displayValue={(userId: string) => {
                            const user = (groupMembersData || []).find((u: any) => u.id === userId);
                            return user ? `${user.firstName} ${user.lastName}` : '';
                          }}
                          onChange={(e) => setAssignedToQuery(e.target.value)}
                          placeholder="Select Assignee"
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setAssignedToQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {(groupMembersData || [])
                              .filter((member: any) => `${member.firstName} ${member.lastName}`.toLowerCase().includes(assignedToQuery.toLowerCase()))
                              .map((member: any) => (
                              <Combobox.Option
                                key={member.id}
                                value={member.id}
                                className={({ active }) =>
                                  `relative cursor-pointer select-none py-2 pl-3 pr-9 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                }
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                      {`${member.firstName} ${member.lastName}`}
                                    </span>
                                    {selected && (
                                      <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                        <CheckIcon className="h-4 w-4" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Combobox.Option>
                            ))}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                    {errors.assignedTo && (
                      <p className="mt-1 text-sm text-red-600">{errors.assignedTo}</p>
                    )}
                  </div>
                </div>
              </div>
              )}
            </div>

            <div className="grid grid-cols-12 gap-6 mb-6">
              {/* Short Description - Full width */}
              <div className="col-span-12">
                <div className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-2">
                    <label htmlFor="shortDescription" className="block text-sm font-medium text-gray-700 mb-1 pt-2">
                      <span className="text-red-500">*</span> Short Description
                    </label>
                  </div>
                  <div className="col-span-10">
                    <input
                      type="text"
                      id="shortDescription"
                      name="shortDescription"
                      value={formData.shortDescription}
                      onChange={handleChange}
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        errors.shortDescription 
                          ? 'border-red-300 focus:ring-red-400' 
                          : 'border-gray-300'
                      }`}
                      placeholder="Brief summary of the case"
                    />
                    {errors.shortDescription && (
                      <p className="mt-1 text-sm text-red-600">{errors.shortDescription}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-12 gap-6 mb-8">
              {/* Description - Full width */}
              <div className="col-span-12">
                <div className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-2">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1 pt-2">
                      Description
                    </label>
                  </div>
                  <div className="col-span-10">
                    <RichTextEditor
                      value={formData.description}
                      onChange={handleDescriptionChange}
                      placeholder="Enter case description"
                      minHeight="120px"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/cases')}
                className="px-6 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createCaseMutation.isPending}
                className="px-6 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {createCaseMutation.isPending ? 'Creating...' : 'Create Case'}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default CaseCreate;
